/**
 * Upload Routes Hook
 * 
 * Custom hook for handling route upload operations with validation, batch processing,
 * progress tracking, and error handling.
 * 
 * @module useUploadRoutes
 */

import { useState, useCallback } from 'react';
import type { FixedRoute, FixedRouteStop } from '../types/fixedRoutes';
import { useFixedRoutes } from '../context/FixedRoutesContext';
import { validateForUpload, type UploadValidationData } from '../services/routesTransferValidation';
import {
  createRouteVersion,
  fetchRouteList,
  applyRouteVersionToShifts,
  createDatedShiftSolutionForRouteUpload,
  createDatedShiftSolution,
  fetchOperationsSolutions,
  ROUTE_UPLOAD_DATED_SHIFT_SOLUTION_COMMAND_SPEC,
  type Environment,
  type CreateDatedShiftSolutionPayload,
  type UATNetworkSettingsResponse,
} from '../services/fixedRouteUatClient';
import { trackAnalyticsEvent } from '../services/analytics';
import { exportRouteData, transformToUATRoutePayload } from '../services/fixedRouteDataExporter';
import { preflightCheckRoute } from '../services/fixedRoute/uploadPreflight';
import {
  extractExistingVehicleBookings,
  findConflictingBooking,
  describeBookingConflict,
  type ExistingVehicleBooking,
  type OpsOperationSolutionLike,
} from '../services/fixedRoute/shiftOverlapPrecheck';
import { calculateRouteGeometryForStops } from '../services/routing/osrmClient';
import { computeRouteGeometryHash } from '../utils/fixedRoutes/routeToLineConverter';
import { handleApiError } from '../utils/errorHandling';
import type {
  RouteOperationResult,
  RouteShiftResult,
} from '../components/map/components/routes-transfer/OperationResults';
import {
  buildDatedShiftCreationIntent,
  buildOperationAssignmentShiftRequests,
  buildRouteNameValidationWarnings,
  isUatEnvironment,
  shouldCreateShiftSolutionLive,
  type UploadShiftAssignmentOptions,
} from '../services/routeUploadShiftAssignment';

/**
 * Max concurrent createrouteversion calls. Keep low so we don't overwhelm
 * the proxy or hit ODB rate limits, but high enough that 10-20 route batches
 * complete in a reasonable time.
 */
const UPLOAD_CONCURRENCY = 4;

export function normaliseRouteName(name: string): string {
  // Replace non-breaking spaces, collapse internal whitespace, then trim.
  // Defensive against MTP-derived names that may contain U+00A0 or stray tabs.
  return name.replace(/ /g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
}

/**
 * Compute the next "Version N+1" name for a route, mirroring ops-portal's
 * generateVersionName() in RouteForm.tsx. Accepts an array of existing version
 * names (e.g. from `latestVersion` field) and returns the next one. Falls back
 * to "Version 1" when no parseable name is present.
 */
export function nextVersionName(existingVersionNames: ReadonlyArray<string>): string {
  let maxN = 0;
  for (const versionName of existingVersionNames) {
    const parts = (versionName ?? '').trim().split(/\s+/);
    if (parts.length === 2 && parts[0].toLowerCase() === 'version') {
      const n = parseInt(parts[1], 10);
      if (!isNaN(n) && n > maxN) maxN = n;
    }
  }
  return `Version ${maxN + 1}`;
}

/** Preview of what an upload will do — surfaced in the confirmation dialog. */
export interface UploadPreview {
  willOverwrite: Array<{
    routeId: string;
    name: string;
    routeVersionName: string;
    existingRouteId: string;
    nextVersionName: string;
  }>;
  willCreate: Array<{ routeId: string; name: string; routeVersionName: string }>;
  /** Reused at upload time so we don't re-fetch the route list. */
  existingRoutesByName: Map<string, { id: string; latestVersion: string }>;
}

/**
 * Upload progress for a single route
 */
export interface RouteUploadProgress {
  routeId: string;
  routeName: string;
  status: 'pending' | 'validating' | 'uploading' | 'success' | 'error';
  message: string;
  progress?: number; // 0-100
}

/**
 * Upload state
 */
export interface UploadState {
  /** Whether upload is in progress */
  isUploading: boolean;
  /** Whether validation is in progress */
  isValidating: boolean;
  /** Upload progress for each route */
  progress: Map<string, RouteUploadProgress>;
  /** Upload results */
  results: RouteOperationResult[];
  /** Validation results */
  validation: UploadValidationData | null;
  /** Current error */
  error: string | null;
}

export interface StartUploadOptions {
  /**
   * Precomputed lookup from `previewUpload`. If omitted, startUpload re-fetches
   * the route list itself (slower; useful for code paths that bypass the
   * confirmation dialog).
   */
  preview?: UploadPreview;
  /**
   * When true (default), each successful version bump on an existing route
   * triggers `applyRouteVersionToShifts` so future shifts point at the new
   * version. Set false to leave shifts on the previous version.
   */
  applyToShifts?: boolean;
  /**
   * Controls post-upload handling for brand-new routes that do not yet have
   * shift assignments in Ops.
   */
  shiftAssignment?: UploadShiftAssignmentOptions;
  /**
   * Optional strict route-name validation toggles.
   */
  routeNameValidation?: {
    elcStrictMode?: boolean;
  };
}

/**
 * Hook return type
 */
export interface UseUploadRoutesReturn {
  /** Upload state */
  uploadState: UploadState;
  /** Start upload operation */
  startUpload: (
    selectedRouteIds: Set<string>,
    prerequisites: {
      jwtToken: string;
      productId: string;
      networkId: number | null;
      networkSettings?: UATNetworkSettingsResponse | null; // Network settings response to get UUID
      serviceDate?: string;
      timezone?: string;
      downloadAcknowledged?: boolean;
      environment: Environment;
    },
    options?: StartUploadOptions,
  ) => Promise<void>;
  /** Validate routes before upload */
  validateRoutes: (
    selectedRouteIds: Set<string>,
    prerequisites: {
      jwtToken: string;
      productId: string;
      networkId: number | null;
      serviceDate?: string;
      timezone?: string;
      downloadAcknowledged?: boolean;
    }
  ) => Promise<UploadValidationData | null>;
  /**
   * Fetch the Ops Portal route list and classify the selected routes into
   * willOverwrite (name matches an existing route → new version) vs
   * willCreate (no match → brand-new route). Called by the confirmation
   * dialog to show the user what's about to happen.
   */
  previewUpload: (
    selectedRouteIds: Set<string>,
    prerequisites: {
      jwtToken: string;
      productId: string;
      environment: Environment;
    },
  ) => Promise<UploadPreview>;
  /** Reset upload state */
  resetUpload: () => void;
}

/**
 * Page through `getroutelist` until exhausted and build a normalised
 * name → { id, latestVersion } map. Use this to look up an existing route's
 * id (for upsert) and its latest version name (to compute next version).
 */
async function fetchAllRoutesIndexedByName(
  jwtToken: string,
  productId: string,
  environment: Environment,
): Promise<Map<string, { id: string; latestVersion: string }>> {
  const PAGE_SIZE = 500;
  const result = new Map<string, { id: string; latestVersion: string }>();
  let offset = 0;
  // Hard ceiling so a bad response can't loop forever.
  for (let page = 0; page < 50; page++) {
    const response = await fetchRouteList(
      jwtToken,
      productId,
      offset,
      PAGE_SIZE,
      'label',
      'DESC',
      environment,
    );
    const entries = response.data?.entries ?? [];
    for (const entry of entries) {
      if (entry.label && entry.id) {
        result.set(normaliseRouteName(entry.label), {
          id: entry.id,
          latestVersion: entry.latestVersion || '',
        });
      }
    }
    if (entries.length < PAGE_SIZE) break;
    offset += PAGE_SIZE;
  }
  return result;
}

function getLatestVersionForRoute(
  existingRoutesByName: Map<string, { id: string; latestVersion: string }>,
  route: FixedRoute,
  routeNameKey: string,
): string {
  if (route.uatRouteId) {
    let latestVersion = '';
    existingRoutesByName.forEach((entry) => {
      if (entry.id === route.uatRouteId) {
        latestVersion = entry.latestVersion;
      }
    });
    if (latestVersion) {
      return latestVersion;
    }
  }
  return existingRoutesByName.get(routeNameKey)?.latestVersion ?? '';
}

/**
 * useUploadRoutes Hook
 *
 * Manages route upload operations with validation, batch processing, and progress tracking.
 */
export function useUploadRoutes(): UseUploadRoutesReturn {
  const { routes, stops, updateRoute } = useFixedRoutes();
  const [uploadState, setUploadState] = useState<UploadState>({
    isUploading: false,
    isValidating: false,
    progress: new Map(),
    results: [],
    validation: null,
    error: null,
  });

  /**
   * Validate routes before upload
   */
  const validateRoutes = useCallback(
    async (
      selectedRouteIds: Set<string>,
      prerequisites: {
        jwtToken: string;
        productId: string;
        networkId: number | null;
        serviceDate?: string;
        timezone?: string;
        downloadAcknowledged?: boolean;
      }
    ): Promise<UploadValidationData | null> => {
      if (selectedRouteIds.size === 0) {
        return null;
      }

      setUploadState((prev) => ({ ...prev, isValidating: true, error: null }));

      try {
        // Get selected routes
        const selectedRoutes = routes.filter((r) => selectedRouteIds.has(r.id));

        // Validate
        const validation = await validateForUpload(selectedRoutes, stops, {
          jwtToken: prerequisites.jwtToken,
          productId: prerequisites.productId,
          networkId: prerequisites.networkId,
          serviceDate: prerequisites.serviceDate,
          timezone: prerequisites.timezone,
          downloadAcknowledged: prerequisites.downloadAcknowledged,
          selectedRouteIds,
        });

        setUploadState((prev) => ({
          ...prev,
          validation,
          isValidating: false,
        }));

        return validation;
      } catch (error) {
        const friendlyError = handleApiError(error, 'Route validation');
        setUploadState((prev) => ({
          ...prev,
          isValidating: false,
          error: friendlyError.message,
        }));
        return null;
      }
    },
    [routes, stops]
  );

  /**
   * Preview an upload — classify each selected route as overwrite (name match
   * in the Ops Portal route list → new version under same routeId) vs create
   * (no match → brand-new route). The dialog uses this to render a per-route
   * breakdown and to compute next-version names.
   */
  const previewUpload = useCallback(
    async (
      selectedRouteIds: Set<string>,
      prerequisites: { jwtToken: string; productId: string; environment: Environment },
    ): Promise<UploadPreview> => {
      const selectedRoutes = routes.filter((r) => selectedRouteIds.has(r.id));
      const existingRoutesByName = await fetchAllRoutesIndexedByName(
        prerequisites.jwtToken,
        prerequisites.productId,
        prerequisites.environment,
      );

      const willOverwrite: UploadPreview['willOverwrite'] = [];
      const willCreate: UploadPreview['willCreate'] = [];

      for (const route of selectedRoutes) {
        const routeNameKey = normaliseRouteName(route.name);
        const match = existingRoutesByName.get(routeNameKey);
        // Local `uatRouteId` takes precedence — it's authoritative for routes
        // we've uploaded from this Atlas instance, even if the name was
        // later changed on the Ops Portal.
        const existingRouteId = route.uatRouteId || match?.id;
        if (existingRouteId) {
          const latestVersion = getLatestVersionForRoute(existingRoutesByName, route, routeNameKey);
          const routeVersionName = nextVersionName([latestVersion]);
          willOverwrite.push({
            routeId: route.id,
            name: route.name,
            routeVersionName,
            existingRouteId,
            nextVersionName: routeVersionName,
          });
        } else {
          willCreate.push({ routeId: route.id, name: route.name, routeVersionName: 'Version 1' });
        }
      }

      return { willOverwrite, willCreate, existingRoutesByName };
    },
    [routes],
  );

  /**
   * Start upload operation
   */
  const startUpload = useCallback(
    async (
      selectedRouteIds: Set<string>,
      prerequisites: {
        jwtToken: string;
        productId: string;
        networkId: number | null;
        networkSettings?: UATNetworkSettingsResponse | null; // Network settings response to get UUID
        serviceDate?: string;
        timezone?: string;
        downloadAcknowledged?: boolean;
        environment: Environment;
      },
      options?: StartUploadOptions,
    ) => {
      const applyToShifts = options?.applyToShifts ?? true;
      const shiftAssignmentMode = options?.shiftAssignment?.mode ?? 'warn-only';
      const routeNameValidation = options?.routeNameValidation;
      if (selectedRouteIds.size === 0) {
        return;
      }

      // Get selected routes
      const selectedRoutes = routes.filter((r) => selectedRouteIds.has(r.id));

      // Initialize progress
      const progressMap = new Map<string, RouteUploadProgress>();
      selectedRoutes.forEach((route) => {
        progressMap.set(route.id, {
          routeId: route.id,
          routeName: route.name,
          status: 'pending',
          message: 'Waiting...',
          progress: 0,
        });
      });

      setUploadState({
        isUploading: true,
        isValidating: false,
        progress: progressMap,
        results: [],
        validation: null,
        error: null,
      });

      // Resolve network UUID once for the whole batch.
      let networkUuid: string | undefined;
      if (prerequisites.networkSettings?.data?.networks && prerequisites.networkId) {
        const matchingNetwork = prerequisites.networkSettings.data.networks.find(
          (n) => n.networkSettings?.dbId === prerequisites.networkId,
        );
        if (matchingNetwork?.networkSettings?.id) {
          networkUuid = matchingNetwork.networkSettings.id;
        }
      }
      if (!networkUuid && prerequisites.networkSettings?.data?.networks?.[0]?.networkSettings?.id) {
        networkUuid = prerequisites.networkSettings.data.networks[0].networkSettings.id;
      }
      if (!networkUuid) {
        setUploadState((prev) => ({
          ...prev,
          isUploading: false,
          error:
            'Network UUID is required. Please ensure network settings are loaded and a network is selected.',
        }));
        return;
      }

      // Pre-batch: build a (normalised route name → { id, latestVersion }) map.
      // Routes that match an entry are uploaded as a new version of the existing
      // route (preserving shifts/drivers, which reference routeId rather than
      // versionId). Routes with no match are created fresh.
      //
      // Prefer the map already computed by previewUpload (avoids a redundant
      // round-trip). Fall back to fetching if the caller bypassed the preview.
      let existingRoutesByName = options?.preview?.existingRoutesByName;
      if (!existingRoutesByName) {
        try {
          existingRoutesByName = await fetchAllRoutesIndexedByName(
            prerequisites.jwtToken,
            prerequisites.productId,
            prerequisites.environment,
          );
        } catch (error) {
          // Non-fatal — we'll fall through to "create new" for unmatched routes.
          console.warn('[useUploadRoutes] Failed to fetch existing route list for upsert detection:', error);
          existingRoutesByName = new Map();
        }
      }

      // ── Shift overlap pre-check (batch-wide) ───────────────────────────────
      // Hailer rejects a createshiftsolution whose vehicle already has a shift
      // over an overlapping date window ("Vehicle not available at this time").
      // Fetch the existing Ops bookings ONCE for the whole batch so we can skip
      // those shifts with a clear "already deployed" message instead of a raw
      // rejection — and so re-uploads are idempotent. Best-effort: a fetch
      // failure just disables the pre-check and lets the live write decide.
      const shiftServiceDate = options?.shiftAssignment?.serviceDate ?? prerequisites.serviceDate;
      const shiftTimezone = options?.shiftAssignment?.timezone ?? prerequisites.timezone;
      let existingBookingsPromise: Promise<ExistingVehicleBooking[]> | null = null;
      const getExistingVehicleBookings = (): Promise<ExistingVehicleBooking[]> => {
        if (!existingBookingsPromise) {
          existingBookingsPromise = (async (): Promise<ExistingVehicleBooking[]> => {
            if (
              shiftAssignmentMode !== 'create-dated-shifts' ||
              !isUatEnvironment(prerequisites.environment) ||
              !shiftServiceDate ||
              !shiftTimezone
            ) {
              return [];
            }
            try {
              const opsResponse = await fetchOperationsSolutions(
                prerequisites.jwtToken,
                prerequisites.productId,
                shiftServiceDate,
                shiftTimezone,
                prerequisites.environment,
              );
              return extractExistingVehicleBookings(
                (opsResponse.data?.entries ?? []) as unknown as OpsOperationSolutionLike[],
              );
            } catch (err) {
              console.warn(
                '[useUploadRoutes] Could not fetch existing shifts for overlap pre-check:',
                err,
              );
              return [];
            }
          })();
        }
        return existingBookingsPromise;
      };
      // Vehicle bookings CREATED during this run — so a vehicle reused across
      // routes/shifts in one upload is skipped after its first successful create,
      // not just ones that pre-existed in Ops.
      const bookingsCreatedThisRun: ExistingVehicleBooking[] = [];

      // Per-route upload helper. Pure async function — safe to run concurrently
      // for distinct route ids. Writes its own progress entry and returns a
      // RouteOperationResult.
      const uploadOne = async (route: FixedRoute): Promise<RouteOperationResult> => {
        const progress = progressMap.get(route.id)!;
        const updateProgress = (patch: Partial<RouteUploadProgress>) => {
          Object.assign(progress, patch);
          setUploadState((prev) => ({ ...prev, progress: new Map(progressMap) }));
        };

        try {
          updateProgress({ status: 'validating', message: 'Validating…', progress: 5 });

          let routeForUpload = route;
          let exportedData = exportRouteData(route, stops);

          // Auto-route if geometry is missing. ODB's createrouteversion rejects
          // empty routeShape with a generic error; routing here side-steps it.
          if (!exportedData.routeShape || exportedData.routeShape.length === 0) {
            const orderedStops = route.stops
              .map((sc) => stops.find((s) => s.id === sc.stopId))
              .filter((s): s is NonNullable<typeof s> => !!s && !!s.coordinates);
            if (orderedStops.length < 2) {
              throw new Error('Route has fewer than 2 stops with coordinates; cannot auto-route.');
            }

            updateProgress({
              message: `Routing via roads (${orderedStops.length - 1} segments)…`,
              progress: 15,
            });

            const segmentGeometries = await calculateRouteGeometryForStops(orderedStops, 'driving');
            const flatCoordinates: [number, number][] = [];
            for (const segment of segmentGeometries) {
              if (flatCoordinates.length > 0 && segment.length > 0) {
                const last = flatCoordinates[flatCoordinates.length - 1];
                const first = segment[0];
                if (
                  Math.abs(last[0] - first[0]) < 0.0001 &&
                  Math.abs(last[1] - first[1]) < 0.0001
                ) {
                  flatCoordinates.push(...segment.slice(1));
                } else {
                  flatCoordinates.push(...segment);
                }
              } else {
                flatCoordinates.push(...segment);
              }
            }
            if (flatCoordinates.length === 0) {
              throw new Error(
                'Auto-routing produced no geometry — check stop coordinates and OSRM availability.',
              );
            }

            routeForUpload = {
              ...route,
              geometry: {
                coordinates: flatCoordinates,
                calculatedAt: Date.now(),
                stopsHash: computeRouteGeometryHash(route, orderedStops),
                segmentGeometries,
              },
            };
            updateRoute(routeForUpload);
            exportedData = exportRouteData(routeForUpload, stops);
          }

          // PRE-UPLOAD PREFLIGHT (FAILURE #1): Hailer's createrouteversion
          // collapses six distinct failures into a single opaque "Creating new
          // RouteVersion failed" string and only logs the cause server-side. For
          // brand-new sim routes every stop is inserted inline, so a non-finite
          // or (0,0) coordinate, an empty label, or empty geometry surfaces as
          // that generic message with no client signal. Fail fast HERE with a
          // precise, per-stop reason instead of round-tripping to Hailer. Runs
          // AFTER auto-routing so an empty shape genuinely means routing failed.
          const preflight = preflightCheckRoute(exportedData);
          if (!preflight.ok) {
            throw new Error(preflight.issues.map((issue) => issue.message).join(' '));
          }

          // Resolve existingRouteId so a re-upload becomes a new version of the
          // existing route instead of a duplicate. Priority: persisted local
          // uatRouteId → name-match against fetched route list → undefined (new).
          const nameKey = normaliseRouteName(route.name);
          const nameMatch = existingRoutesByName!.get(nameKey);
          const existingRouteId = route.uatRouteId || nameMatch?.id;
          const wasUpdate = !!existingRouteId;
          // Diagnostic log — makes it trivial to confirm in devtools whether
          // the upsert decision was made on local cache vs name lookup. Quiet
          // enough to leave on permanently.
          console.info('[useUploadRoutes] upsert resolution', {
            routeName: route.name,
            normalisedName: nameKey,
            hadLocalUatRouteId: !!route.uatRouteId,
            matchedFromList: nameMatch?.id ?? null,
            resolvedExistingRouteId: existingRouteId ?? null,
          });

          updateProgress({
            status: 'uploading',
            message: wasUpdate ? 'Uploading new version…' : 'Uploading…',
            progress: 60,
          });

          // Match ops-portal's generateVersionName(): "Version 1" for brand-new
          // routes, "Version N+1" parsed from the existing route's latestVersion
          // for upserts.
          const versionName = wasUpdate
            ? nextVersionName([getLatestVersionForRoute(existingRoutesByName!, route, nameKey)])
            : 'Version 1';
          const payload = transformToUATRoutePayload(
            exportedData,
            networkUuid!,
            versionName,
            '',
            undefined,
            existingRouteId,
          );

          // Client-generated idempotency key — repeat retries of the same
          // (route, content, network) won't spawn duplicate versions even if
          // the proxy forwards the header. Keyed on routeId + uploadAttemptId
          // so two distinct Upload clicks on the same selection get distinct
          // keys (intentional duplicate would actually need two clicks).
          const idempotencyKey =
            typeof crypto !== 'undefined' && 'randomUUID' in crypto
              ? `atlas-upload-${crypto.randomUUID()}`
              : `atlas-upload-${route.id}-${Date.now()}`;

          const response = await createRouteVersion(
            prerequisites.jwtToken,
            prerequisites.productId,
            payload,
            prerequisites.environment,
            idempotencyKey,
          );

          const responseData = response.data?.data || response.data;
          const createdRouteVersionId = responseData?.createdRouteVersionId || undefined;
          const uatRouteId = responseData?.routeId || existingRouteId;
          const routeNameWarnings = buildRouteNameValidationWarnings(
            route.name,
            !!routeNameValidation?.elcStrictMode,
          );

          // Persist uatRouteId back to the Atlas route so future
          // uploads skip the name-lookup pre-flight.
          if (uatRouteId && route.uatRouteId !== uatRouteId) {
            updateRoute({ ...routeForUpload, uatRouteId });
          }

          // Point future shifts at the new version (matches ops-portal's
          // post-save "Apply new version" modal). Only relevant for upserts —
          // a brand-new route has no shifts attached yet.
          let shiftUpdateApplied = false;
          let shiftUpdateError: string | undefined;
          if (wasUpdate && applyToShifts && createdRouteVersionId) {
            try {
              const applyResult = await applyRouteVersionToShifts(
                prerequisites.jwtToken,
                prerequisites.productId,
                createdRouteVersionId,
                prerequisites.environment,
              );
              if (!applyResult.success) {
                shiftUpdateError = applyResult.error || 'Failed to apply the new version to future shifts.';
                console.warn(
                  '[useUploadRoutes] applyRouteVersionToShifts failed for route',
                  route.name,
                  applyResult.error,
                );
              } else {
                shiftUpdateApplied = true;
              }
            } catch (err) {
              // Non-fatal: the version was created successfully; shift
              // re-application is a follow-up step.
              shiftUpdateError = err instanceof Error ? err.message : String(err);
              console.warn(
                '[useUploadRoutes] applyRouteVersionToShifts threw for route',
                route.name,
                err,
              );
            }
          }

          let datedShiftAttempted = false;
          let datedShiftCreated = false;
          let datedShiftSolutionId: string | undefined;
          let datedShiftWarning: string | undefined;
          let datedShiftError: string | undefined;
          let shiftResults: RouteShiftResult[] | undefined;

          // A route can carry an assigned plan (`operationAssignments`) regardless
          // of whether this upload created the route fresh or bumped its version.
          const hasOperationAssignments = !!route.operationAssignments?.length;

          if (createdRouteVersionId && shiftAssignmentMode === 'create-dated-shifts') {
            if (hasOperationAssignments) {
              // PRIMARY PATH: derive one shift-solution request per distinct shift
              // solution FROM the route's assigned operations (Phase B). Reuses the
              // pure preview helpers (expandDraftToPreview + collapsePreviewRows) so
              // the request shape matches the modal preview exactly.
              const shiftRequests = buildOperationAssignmentShiftRequests({
                route,
                routeVersionId: createdRouteVersionId,
                // FAILURE #2 (PRIMARY): the operation chosen in the UPLOAD dialog
                // is authoritative when the assigned plan omitted one (the assign
                // modal makes the operation optional). Thread it in so blank
                // per-route operationSolutionIds are backfilled instead of being
                // silently skipped by the live-write gate.
                fallbackOperationSolutionId: options?.shiftAssignment?.operationSolutionId,
                fallbackOperationName: options?.shiftAssignment?.operationSolutionName,
              });

              const perShift: RouteShiftResult[] = [];
              for (const { label, request } of shiftRequests) {
                datedShiftAttempted = true;

                // PHASE C: live shift creation is UAT-ONLY and requires both a
                // chosen operation AND a mapped vehicle (Hailer rejects otherwise).
                // Production stays gated (no write); missing operation/vehicle is
                // skipped with a per-shift warning rather than attempted.
                if (
                  shouldCreateShiftSolutionLive(prerequisites.environment, {
                    operationSolutionId: request.operationSolutionId,
                    vehicleId: request.vehicleId,
                  })
                ) {
                  // PRE-CHECK: skip when this vehicle is already booked for an
                  // overlapping window — pre-existing in Ops OR created earlier in
                  // this run. Avoids Hailer's opaque "Vehicle not available at this
                  // time" rejection and makes re-uploads idempotent.
                  const existingBookings = await getExistingVehicleBookings();
                  const conflict =
                    findConflictingBooking(request, existingBookings) ??
                    findConflictingBooking(request, bookingsCreatedThisRun);
                  if (conflict) {
                    perShift.push({
                      label,
                      created: false,
                      skipped: true,
                      warning: describeBookingConflict(request.vehicleId ?? 'vehicle', conflict),
                    });
                    continue;
                  }

                  try {
                    // REAL LIVE WRITE — POSTs `createshiftsolution` to UAT Ops.
                    const shiftResponse = await createDatedShiftSolution(
                      prerequisites.jwtToken,
                      prerequisites.productId,
                      ROUTE_UPLOAD_DATED_SHIFT_SOLUTION_COMMAND_SPEC,
                      request,
                      prerequisites.environment,
                    );
                    if (shiftResponse.success) {
                      datedShiftCreated = true;
                      datedShiftSolutionId =
                        shiftResponse.shiftSolutionId ?? datedShiftSolutionId;
                      // Record so a later shift reusing this vehicle in THIS run
                      // is skipped (the fetched snapshot predates this create).
                      if (request.vehicleId?.trim()) {
                        bookingsCreatedThisRun.push({
                          vehicleId: request.vehicleId.trim(),
                          startDate: request.startDate,
                          endDate: request.endDate,
                          shiftSolutionId: shiftResponse.shiftSolutionId,
                        });
                      }
                      perShift.push({
                        label,
                        created: true,
                        shiftSolutionId: shiftResponse.shiftSolutionId,
                      });
                    } else {
                      // Surface Hailer messages verbatim (e.g. "Vehicle/Driver not
                      // available at this time").
                      perShift.push({
                        label,
                        created: false,
                        error: shiftResponse.error || 'Failed to create dated shift solution.',
                      });
                    }
                  } catch (err) {
                    const message =
                      err instanceof Error ? err.message : 'Failed to create dated shift solution.';
                    perShift.push({ label, created: false, error: message });
                    console.warn(
                      '[useUploadRoutes] createDatedShiftSolution failed for route',
                      route.name,
                      label,
                      err,
                    );
                  }
                } else {
                  // GATED — no write. Explain WHY so the user can act.
                  let warning: string;
                  if (!isUatEnvironment(prerequisites.environment)) {
                    warning = 'PROD shift creation not enabled — UAT only.';
                  } else if (!request.operationSolutionId?.trim()) {
                    warning = 'Shift not created — select an operation.';
                  } else {
                    warning = 'Shift not created — map a vehicle.';
                  }
                  perShift.push({ label, created: false, warning });
                }
              }

              shiftResults = perShift;
              const skippedCount = perShift.filter((s) => s.skipped).length;
              // Gated (no write) excludes pre-check skips, which have their own count.
              const pendingCount = perShift.filter((s) => !s.created && s.warning && !s.skipped).length;
              const failedCount = perShift.filter((s) => s.error).length;
              const createdCount = perShift.filter((s) => s.created).length;
              if (failedCount > 0) {
                datedShiftError = `${failedCount} of ${perShift.length} shift solution(s) failed.`;
              }
              const warningParts: string[] = [];
              if (pendingCount > 0) {
                warningParts.push(`${pendingCount} not created (gated; no write performed)`);
              }
              if (skippedCount > 0) {
                warningParts.push(`${skippedCount} already deployed (skipped)`);
              }
              if (warningParts.length > 0) {
                datedShiftWarning = `${warningParts.join('; ')} of ${perShift.length} shift solution(s).`;
              }

              // Analytics (repo policy for new behavior): one event per route after
              // the shift-creation loop. Counts only — no vehicle regos beyond what
              // upstream already sends. Fired for UAT (live) AND PROD (gated) so the
              // deploy funnel is complete.
              trackAnalyticsEvent('fixed_route_shifts_deployed', {
                environment: prerequisites.environment,
                route_id: route.id,
                shifts_attempted: perShift.length,
                shifts_created: createdCount,
                shifts_failed: failedCount,
                shifts_skipped: skippedCount,
                timestamp: new Date().toISOString(),
              });
            } else if (!wasUpdate) {
              // FALLBACK PATH: route has no assigned operations — use the legacy
              // single-intent path built from preserved import metadata. Only
              // relevant for brand-new routes (an upsert keeps its existing shifts).
              const datedShiftIntent = buildDatedShiftCreationIntent({
                route,
                routeVersionId: createdRouteVersionId,
                options: options?.shiftAssignment ?? { mode: 'warn-only' },
              });

              if (!datedShiftIntent) {
                datedShiftWarning =
                  'Dated shift creation is enabled, but operation/date context is incomplete. Assign shifts manually in Ops.';
              } else {
                datedShiftAttempted = true;
                const datedShiftPayload: CreateDatedShiftSolutionPayload = {
                  operationSolutionId: datedShiftIntent.operationSolutionId,
                  routeVersionId: datedShiftIntent.routeVersionId,
                  startTime: datedShiftIntent.startTime,
                  endTime: datedShiftIntent.endTime,
                  timezone: datedShiftIntent.timezone,
                  vehicleId: datedShiftIntent.vehicleId,
                };
                try {
                  // STUBBED/GATED (same invariant as the operationAssignments path
                  // above): pass `null` as the command spec so this returns 501
                  // integrationPending and writes NOTHING to Ops. The function's
                  // DEFAULT 5th arg is the REAL `ROUTE_UPLOAD_DATED_SHIFT_SOLUTION_COMMAND_SPEC`,
                  // which would POST a live `createshiftsolution` — so the `null`
                  // here is load-bearing. Un-stubbing + UAT QA is Phase C.
                  const shiftResponse = await createDatedShiftSolutionForRouteUpload(
                    prerequisites.jwtToken,
                    prerequisites.productId,
                    datedShiftPayload,
                    prerequisites.environment,
                    null,
                  );
                  if (shiftResponse.success) {
                    datedShiftCreated = true;
                    datedShiftSolutionId = shiftResponse.createdShiftSolutionId;
                  } else if (shiftResponse.integrationPending) {
                    datedShiftWarning = shiftResponse.error;
                  } else {
                    datedShiftError = shiftResponse.error || 'Failed to create dated shift solution.';
                  }
                } catch (err) {
                  datedShiftError =
                    err instanceof Error ? err.message : 'Failed to create dated shift solution.';
                  console.warn(
                    '[useUploadRoutes] createDatedShiftSolutionForRouteUpload failed for route',
                    route.name,
                    err,
                  );
                }
                if (datedShiftIntent.warnings.length > 0) {
                  datedShiftWarning = [...datedShiftIntent.warnings, datedShiftWarning]
                    .filter(Boolean)
                    .join(' ');
                }
              }
            }
          } else if (!wasUpdate && createdRouteVersionId) {
            datedShiftWarning =
              'New route uploaded without dated shift creation. Manual shift assignment is required in Ops.';
          }

          updateProgress({
            status: 'success',
            message: wasUpdate
              ? `New version: ${createdRouteVersionId || 'success'}`
              : `Created: ${createdRouteVersionId || 'success'}`,
            progress: 100,
          });

          return {
            routeId: route.id,
            routeName: route.name,
            success: true,
            createdRouteVersionId,
            uatRouteId: uatRouteId || undefined,
            routeVersionName: versionName,
            shiftUpdateApplied: wasUpdate ? shiftUpdateApplied : undefined,
            shiftUpdateError: wasUpdate ? shiftUpdateError : undefined,
            // Dated-shift fields surface for new routes (legacy single-intent path)
            // AND for any route whose assigned operations drove the shift requests
            // (the operationAssignments path can run for upserts too).
            datedShiftAttempted: !wasUpdate || shiftResults ? datedShiftAttempted : undefined,
            datedShiftCreated: !wasUpdate || shiftResults ? datedShiftCreated : undefined,
            datedShiftSolutionId: !wasUpdate || shiftResults ? datedShiftSolutionId : undefined,
            datedShiftWarning: !wasUpdate || shiftResults ? datedShiftWarning : undefined,
            datedShiftError: !wasUpdate || shiftResults ? datedShiftError : undefined,
            shiftResults,
            wasUpdate,
            warnings: routeNameWarnings.length > 0 ? routeNameWarnings : undefined,
          };
        } catch (error) {
          const friendlyError = handleApiError(error, `Upload route "${route.name}"`);
          updateProgress({
            status: 'error',
            message: friendlyError.message,
            progress: 100,
          });
          return {
            routeId: route.id,
            routeName: route.name,
            success: false,
            error: friendlyError.message,
          };
        }
      };

      // Run uploads with bounded concurrency. Promise.allSettled inside each
      // chunk means one bad route doesn't block the rest of the chunk; chunks
      // run sequentially so we never exceed UPLOAD_CONCURRENCY parallel calls.
      const results: RouteOperationResult[] = [];
      for (let i = 0; i < selectedRoutes.length; i += UPLOAD_CONCURRENCY) {
        const chunk = selectedRoutes.slice(i, i + UPLOAD_CONCURRENCY);
        const chunkResults = await Promise.all(chunk.map(uploadOne));
        results.push(...chunkResults);
      }

      // Finalize
      setUploadState((prev) => ({
        ...prev,
        isUploading: false,
        results,
      }));
    },
    [routes, stops, updateRoute]
  );

  /**
   * Reset upload state
   */
  const resetUpload = useCallback(() => {
    setUploadState({
      isUploading: false,
      isValidating: false,
      progress: new Map(),
      results: [],
      validation: null,
      error: null,
    });
  }, []);

  return {
    uploadState,
    startUpload,
    validateRoutes,
    previewUpload,
    resetUpload,
  };
}
